import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, ArrowUpRight, Replace, Search } from 'lucide-react'
import { WorkspaceDialog } from './WorkspaceDialog'
import {
  findStoryMatches,
  replaceStoryMatches,
  type StoryMatch,
} from '../findReplace'
import type { StoryProject } from '../types'

export function FindReplace({
  project,
  selectedNodeId,
  onChange,
  onClose,
  onOpenMatch,
}: {
  project: StoryProject
  selectedNodeId: string
  onChange: (project: StoryProject, historyKey?: string) => void
  onClose: () => void
  onOpenMatch: (match: StoryMatch) => void
}) {
  const [query, setQuery] = useState('')
  const [replacement, setReplacement] = useState('')
  const [caseSensitive, setCaseSensitive] = useState(false)
  const [wholeWord, setWholeWord] = useState(false)
  const [includePlanning, setIncludePlanning] = useState(false)
  const [sceneOnly, setSceneOnly] = useState(false)
  const [index, setIndex] = useState(0)
  const [notice, setNotice] = useState('')
  const [confirmAll, setConfirmAll] = useState(false)
  const matches = useMemo(
    () =>
      findStoryMatches(project, query, {
        caseSensitive,
        wholeWord,
        includePlanning,
        nodeId: sceneOnly ? selectedNodeId : undefined,
      }),
    [
      project,
      query,
      caseSensitive,
      wholeWord,
      includePlanning,
      sceneOnly,
      selectedNodeId,
    ],
  )
  const selectedIndex = Math.min(index, Math.max(0, matches.length - 1))
  const match = matches[selectedIndex]
  const updated = () => {
    setIndex(0)
    setNotice('')
    setConfirmAll(false)
  }
  const snippet = (value: string, start: number, end: number) => (
    <>
      {start > 60 ? '…' : ''}
      {value.slice(Math.max(0, start - 60), start)}
      <mark>{value.slice(start, end) || '∅'}</mark>
      {value.slice(end, end + 100)}
      {end + 100 < value.length ? '…' : ''}
    </>
  )
  const replace = (all: boolean) => {
    const selected = all ? matches : match ? [match] : []
    const next = replaceStoryMatches(project, selected, replacement)
    if (next !== project) onChange(next, 'replace:' + crypto.randomUUID())
    setNotice(
      next === project
        ? 'The replacement leaves this text unchanged.'
        : `Replaced ${selected.length} ${selected.length === 1 ? 'match' : 'matches'}. Undo is available when you return to writing.`,
    )
    setConfirmAll(false)
  }
  return (
    <WorkspaceDialog
      title="Find & replace"
      eyebrow="A SMALL CHANGE, EVERYWHERE IT BELONGS"
      onClose={onClose}
      className="find-replace-dialog"
    >
      <div className="find-replace-body">
        <div className="find-replace-fields">
          <label className="dialog-field">
            Find
            <input
              aria-label="Find text"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                updated()
              }}
              placeholder="A name, a phrase, a word…"
            />
          </label>
          <label className="dialog-field">
            Replace with
            <input
              aria-label="Replace with"
              value={replacement}
              onChange={(event) => {
                setReplacement(event.target.value)
                setNotice('')
                setConfirmAll(false)
              }}
              placeholder="Leave empty to remove the match"
            />
          </label>
        </div>
        <div className="find-options">
          <label>
            <input
              type="checkbox"
              checked={caseSensitive}
              onChange={(event) => {
                setCaseSensitive(event.target.checked)
                updated()
              }}
            />{' '}
            Match case
          </label>
          <label>
            <input
              type="checkbox"
              checked={wholeWord}
              onChange={(event) => {
                setWholeWord(event.target.checked)
                updated()
              }}
            />{' '}
            Whole words
          </label>
          <label>
            <input
              type="checkbox"
              checked={includePlanning}
              onChange={(event) => {
                setIncludePlanning(event.target.checked)
                updated()
              }}
            />{' '}
            Include scene notes
          </label>
          <label>
            <input
              type="checkbox"
              checked={sceneOnly}
              onChange={(event) => {
                setSceneOnly(event.target.checked)
                updated()
              }}
            />{' '}
            Current scene only
          </label>
        </div>
        <div className="find-result-heading">
          <strong aria-live="polite">
            {matches.length} {matches.length === 1 ? 'match' : 'matches'}
          </strong>
          <span>
            {matches.length ? `${selectedIndex + 1} of ${matches.length}` : ''}
          </span>
          <button
            type="button"
            className="icon-button"
            aria-label="Previous match"
            disabled={matches.length < 2}
            onClick={() => {
              setIndex((selectedIndex - 1 + matches.length) % matches.length)
              setConfirmAll(false)
            }}
          >
            <ArrowUp size={15} />
          </button>
          <button
            type="button"
            className="icon-button"
            aria-label="Next match"
            disabled={matches.length < 2}
            onClick={() => {
              setIndex((selectedIndex + 1) % matches.length)
              setConfirmAll(false)
            }}
          >
            <ArrowDown size={15} />
          </button>
        </div>
        {match ? (
          <div className="find-result">
            <header>
              <div>
                <strong>{match.nodeTitle}</strong>
                <small>{match.label}</small>
              </div>
              <button type="button" onClick={() => onOpenMatch(match)}>
                Open in draft <ArrowUpRight size={13} />
              </button>
            </header>
            <div className="find-snippet">
              <span>Current text</span>
              <p>{snippet(match.value, match.start, match.end)}</p>
            </div>
            <div className="find-snippet find-snippet--replacement">
              <span>After replacement</span>
              <p>
                {snippet(
                  match.value.slice(0, match.start) +
                    replacement +
                    match.value.slice(match.end),
                  match.start,
                  match.start + replacement.length,
                )}
              </p>
            </div>
          </div>
        ) : (
          <div className="find-empty">
            <Search size={24} />
            <p>
              {query
                ? 'No matches in this scope. Try a different phrase or search the whole story.'
                : 'Search your manuscript, dialogue, character cues, and player choices.'}
            </p>
          </div>
        )}
        {notice && (
          <p className="dialog-message" role="status">
            {notice}
          </p>
        )}
        {confirmAll && (
          <div className="replace-confirm" role="alert">
            <strong>Replace all {matches.length} matches?</strong>
            <p>
              This applies the previewed replacement across{' '}
              {new Set(matches.map((item) => item.nodeId)).size} scenes
              {includePlanning ? ', including scene notes' : ''}.
            </p>
            <button
              type="button"
              className="button"
              onClick={() => setConfirmAll(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="button button--primary"
              onClick={() => replace(true)}
            >
              Confirm replace all
            </button>
          </div>
        )}
      </div>
      <footer className="workspace-dialog-actions">
        <button
          type="button"
          className="button"
          disabled={!matches.length}
          onClick={() => replace(false)}
        >
          <Replace size={14} /> Replace match
        </button>
        <button
          type="button"
          className="button button--primary"
          disabled={!matches.length}
          onClick={() => setConfirmAll(true)}
        >
          Replace all…
        </button>
      </footer>
    </WorkspaceDialog>
  )
}
